import Sentry from "@similarweb/sentry-logger";

import { MODE } from "app/pages/segments/analysis/SegmentsAnalysisTrafficContainerMode";
import type { ICustomSegment, ICustomSegmentsGroup } from "app/services/segments/segmentsApiService";
import { EMPTY_CUSTOM_SEGMENTS } from "app/services/segments/segmentsApiService";
import { SegmentsUtils } from "app/services/segments/SegmentsUtils";
import { SwRedux } from "app/services/SwRedux";
import { proNavigator } from "single-spa/react-app/ProNavigator";

type ExtraParams = Record<string, string | number>;

export const resolveSegmentsTrackingAttributes = (): ExtraParams => {
    const params = proNavigator.getParams();
    const { id, mode } = params;

    if (!id || !mode) {
        return {};
    }

    const swRedux = SwRedux.getInstance();
    const state = swRedux.getState();
    const segmentsModule = state?.segmentsModule || {};
    const customSegmentsMeta = segmentsModule.customSegmentsMeta || EMPTY_CUSTOM_SEGMENTS;
    const userSegments = customSegmentsMeta.Segments || [];
    const accountSegments = customSegmentsMeta.AccountSegments || [];
    const segmentGroups = customSegmentsMeta.SegmentGroups || [];

    // Include AccountSegments so shared/account-level segments resolve too, not just owned ones.
    const allSegments = {
        segments: [...userSegments, ...accountSegments],
    };

    const getSingleAttributes = (): ExtraParams => {
        const segment: ICustomSegment | undefined = SegmentsUtils.getSegmentById(allSegments, id);

        if (!segment) {
            Sentry.captureException(new Error(`Segment not found`), {
                extra: {
                    segmentId: id,
                },
            });
            return {};
        }

        const extraParams: ExtraParams = {
            Segment_Domain: segment.domain || "",
            Segment_Name: segment.segmentName || "",
            Segment_ID: id,
            Segment_Type: "custom",
        };

        return extraParams;
    };

    const getGroupAttributes = (): ExtraParams => {
        const segmentGroup: ICustomSegmentsGroup | undefined = SegmentsUtils.getSegmentGroupById(segmentGroups, id);
        if (!segmentGroup) {
            Sentry.captureException(new Error(`Segment group with id ${id} not found`));
            return {};
        }

        const segments = segmentGroup.segments || [];
        const websites = segmentGroup.websites || [];

        const extraParams: ExtraParams = {
            SegmentComparison_ID: id,
            domains_amount: websites.length,
            segments_amount: segments.length,
            entities_amount: segments.length + websites.length,
        };

        return extraParams;
    };

    const segmentsTrackingAttributes = mode === MODE.single ? getSingleAttributes() : getGroupAttributes();
    return segmentsTrackingAttributes;
};
